<template>
  <div class="d-flex w-100 justify-space-between align-center">
    <p class="subtitle grey bold-medium">{{ t('bookingDetails.datesOf') }}</p>
    <ion-button expand="block" color="grey" fill="clear" @click="isOpen = true"
      ><ion-icon :icon="pencil" size="small"></ion-icon
    ></ion-button>
  </div>

  <ion-modal
    :is-open="isOpen"
    :initial-breakpoint="1"
    :breakpoints="[0.25, 0.5, 0.75, 1]"
    @will-dismiss="onDismiss"
  >
    <div class="d-flex block bg-white">
      <div>
        <div class="ion-padding-bottom d-flex w-100 justify-space-center">
          <p class="font-md bold-medium blue-darker">{{ t('booking.editDates.title') }}</p>
        </div>
        <p class="subtitle grey ion-padding-bottom">
          {{ t('booking.editDates.description') }}
        </p>

        <ion-row class="mt-sm">
          <ion-col size="12">
            <CustomInput
              :min-date="dayjs().format('YYYY-MM-DD')"
              :hide-clear="true"
              :value="startAvailabilityDate"
              v-model="startAvailabilityDate"
              form-name="personalize"
              type="date"
              input-type="date"
              :label="t('search.arrival')"
              name="name"
              class-name="custom-field"
              :placeholder="t('search.arrival')"
              :clear-field="false"
              @update:modelValue="changeStart()"
            />
          </ion-col>
          <ion-col size="12">
            <CustomInput
              :min-date="startAvailabilityDate"
              :hide-clear="true"
              :disabled="!startAvailabilityDate"
              :value="endAvailabilityDate"
              v-model="endAvailabilityDate"
              form-name="personalize"
              type="date"
              input-type="date"
              :label="t('search.departure')"
              name="name"
              class-name="custom-field"
              :placeholder="t('search.departure')"
              :clear-field="false"
            />
          </ion-col>
        </ion-row>

        <div class="d-flex justify-space-between w-100 mt-xs">
          <ion-button
            fill="clear"
            expand="block"
            size="default"
            color="primary"
            class="text-lg text-weight-bold"
            @click="onDismiss()"
          >
            {{ t('actions.cancel') }}
          </ion-button>

          <ion-button
            @click="onSaveChanges()"
            :disabled="endAvailabilityDate === '' || startAvailabilityDate === ''"
            fill="solid"
            expand="block"
            size="default"
            color="primary"
            class="text-lg text-weight-bold"
          >
            {{ t('actions.confirm') }}
          </ion-button>
        </div>
      </div>
    </div>
  </ion-modal>

  <ion-row @click="isOpen = true" class="ba-grey pa-xs border-default cursor-pointer">
    <ion-col size="6" class="ion-no-padding">
      <p class="subtitle grey">{{ t('search.arrival') }}</p>
      <div class="d-flex">
        <ion-icon :icon="calendarClearOutline" color="grey"></ion-icon>
        <p class="padding-list-item font-sm">
          {{
            selectedFilters.mappedFilters.startAvailabilityDate
              ? formatDateTime(
                  selectedFilters.mappedFilters.startAvailabilityDate as string,
                  locale,
                  true,
                  true,
                )
              : '-'
          }}
        </p>
      </div>
    </ion-col>
    <ion-col size="6" class="ion-no-padding padding-list-item-sm">
      <p class="subtitle grey">{{ t('search.departure') }}</p>
      <div class="d-flex">
        <ion-icon :icon="calendarClearOutline" color="grey"></ion-icon>
        <p class="padding-list-item font-sm">
          {{
            selectedFilters.mappedFilters.endAvailabilityDate
              ? formatDateTime(
                  selectedFilters.mappedFilters.endAvailabilityDate as string,
                  locale,
                  true,
                  true,
                )
              : '-'
          }}
        </p>
      </div>
    </ion-col>
  </ion-row>
</template>

<script setup lang="ts">
  import { useI18n } from 'vue-i18n';
  import { ref } from 'vue';
  import { calendarClearOutline, pencil } from 'ionicons/icons';
  import { formatDateTime } from '@/utils/validations/date-utils';
  import { storeToRefs } from 'pinia';
  import { useElasticStore } from '@/stores/elastic-store';
  import { IonButton, IonIcon, IonRow, IonCol, IonModal } from '@ionic/vue';
  import CustomInput from '../shared/form/CustomInput.vue';
  import dayjs from 'dayjs';

  const { selectedFilters } = storeToRefs(useElasticStore());
  const { t, locale } = useI18n();

  const endAvailabilityDate = ref('');
  const startAvailabilityDate = ref('');
  const isOpen = ref(false);

  const { addOrClearFilter } = useElasticStore();

  const onSaveChanges = async () => {
    await addOrClearFilter('startAvailabilityDate', true, startAvailabilityDate.value);
    await addOrClearFilter('endAvailabilityDate', true, endAvailabilityDate.value);

    onDismiss();
  };

  const onDismiss = () => {
    isOpen.value = false;
  };

  const changeStart = () => {
    const startDate = dayjs(startAvailabilityDate.value);
    const endDate = dayjs(endAvailabilityDate.value);

    const isAfter = startDate.isAfter(endDate);

    if (isAfter) {
      endAvailabilityDate.value = '';
    }
  };
</script>

<style lang="css" scoped>
  .block {
    width: 100%;
    height: 100%;
    display: flex;
    padding: 40px 24px;
    overflow: auto;
    text-align: start;
  }
  ion-modal {
    --height: auto;
  }

  .padding-list-item-sm {
    padding-left: 10px;
  }
</style>
